import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, DatePicker, Table } from 'antd';
import moment, { Moment } from 'moment';
import {
  ColumnsType,
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { RangeValue } from 'rc-picker/lib/interface';
import pluralize from 'pluralize';
import queryString from 'query-string';
import cx from 'classnames';
import { useDidUpdate } from 'rooks';

import { pageDefaultSizeTable } from 'constants/index';
import { SortingDirectionEnum } from 'enums';
import useSortColumns, { ISortParams } from 'hooks/useSortColumns';
import { DateParams } from 'utils/filterUtils';
import {
  getDateWithoutTime,
  getDateRangeTillLastSunday,
  disableDatesExceptSundays,
} from 'utils/dateUtils';
import {
  getBillingProfitReportData,
  getBillingProfitReportExcel,
} from 'redux/reducers/homeDashboardSlice';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import LastSyncDateSection from 'components/LastSyncDateSection';
import {
  BillingAndProfitSortFieldEnum,
  BillingAndProfitByCustomerReportViewModel,
  BillingAndProfitByCustomerReportItemViewModel,
} from 'api/backofficeApiClient';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './CustomerBillingProfitReportDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

enum BillingProfitFilterTypes {
  Date = 'date',
}

type BillingProfitReportFilters = {
  [BillingProfitFilterTypes.Date]: DateParams | undefined;
};

const defaultSortParams: ISortParams<BillingAndProfitSortFieldEnum> = {
  sortOrder: 'descend',
  sortBy: BillingAndProfitSortFieldEnum.Billing,
};

const dateRangeDurationInWeeks = 7;

export default function CustomerBillingProfitReportDetailsPage() {
  const [
    isBillingProfitReportDataLoading,
    setIsBillingProfitReportDataLoading,
  ] = useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [billingProfitReportData, setBillingProfitReportData] =
    useState<BillingAndProfitByCustomerReportViewModel>();
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const { sortBy, sortOrder, startDate, endDate } = queryString.parse(
    location.search,
  );
  const defaultDateFilter: DateParams = getDateRangeTillLastSunday(
    dateRangeDurationInWeeks,
  );
  const initialFilters = {
    [BillingProfitFilterTypes.Date]:
      startDate && endDate
        ? {
            startDate: moment(startDate, 'MM/DD/YYYY'),
            endDate: moment(endDate, 'MM/DD/YYYY'),
          }
        : defaultDateFilter,
  };
  const [filters, setFilters] =
    useState<BillingProfitReportFilters>(initialFilters);
  const initialSortParams: ISortParams<BillingAndProfitSortFieldEnum> =
    sortBy && sortOrder
      ? {
          sortBy: parseInt(sortBy),
          sortOrder,
        }
      : defaultSortParams;
  const dispatch = useAppDispatch();

  const billingAndProfitReportCountMessage = pluralize(
    'customer',
    billingProfitReportData?.totalCount || 0,
    true,
  );

  const columns: ColumnsType<BillingAndProfitByCustomerReportItemViewModel> = [
    {
      title: 'Customer',
      key: BillingAndProfitSortFieldEnum.Customer,
      render: (record: BillingAndProfitByCustomerReportItemViewModel) => (
        <Link className="wrapped-text" to={`/customer/${record.customerKey}`}>
          {record.customerName}
        </Link>
      ),
    },
    {
      title: 'Billing $',
      key: BillingAndProfitSortFieldEnum.Billing,
      render: (record: BillingAndProfitByCustomerReportItemViewModel) =>
        record.billing,
    },
    {
      title: 'Member 1 Profit $',
      key: BillingAndProfitSortFieldEnum.Profit,
      render: (record: BillingAndProfitByCustomerReportItemViewModel) =>
        record.profit,
    },
    {
      title: 'Profit %',
      key: BillingAndProfitSortFieldEnum.ProfitPercentage,
      render: (record: BillingAndProfitByCustomerReportItemViewModel) =>
        record.percentageOfProfit,
    },
  ];

  const { sortColumns, sortParams, setSortParams } = useSortColumns(
    columns,
    initialSortParams,
  );

  useDidUpdate(() => {
    const queryData = {
      sortBy: sortParams.sortBy,
      sortOrder: sortParams.sortOrder,
      startDate: filters[BillingProfitFilterTypes.Date]?.startDate
        ? filters[BillingProfitFilterTypes.Date]?.startDate?.format(
            'MM/DD/YYYY',
          )
        : undefined,
      endDate: filters[BillingProfitFilterTypes.Date]?.endDate
        ? filters[BillingProfitFilterTypes.Date]?.endDate?.format('MM/DD/YYYY')
        : undefined,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParams.sortBy, sortParams.sortOrder, filters]);

  const loadBillingProfitReportData = useCallback(async () => {
    setIsBillingProfitReportDataLoading(true);
    const result = await dispatch(
      getBillingProfitReportData({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[BillingProfitFilterTypes.Date]?.startDate
          ? getDateWithoutTime(
              filters[BillingProfitFilterTypes.Date]?.startDate,
            )
          : undefined,
        endDate: filters[BillingProfitFilterTypes.Date]?.endDate
          ? getDateWithoutTime(filters[BillingProfitFilterTypes.Date]?.endDate)
          : undefined,
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getBillingProfitReportData.fulfilled.match(result)) {
      setBillingProfitReportData(result.payload);
    } else {
      setBillingProfitReportData({
        items: [],
        totalCount: 0,
        grandTotal: undefined,
      });
    }

    setIsBillingProfitReportDataLoading(false);
  }, [
    dispatch,
    currentTablePage,
    sortParams.sortOrder,
    sortParams.sortBy,
    filters,
  ]);

  const loadInitialData = useCallback(async () => {
    await loadBillingProfitReportData();

    setIsInitialDataLoaded(true);
  }, [loadBillingProfitReportData]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort:
      | SorterResult<BillingAndProfitByCustomerReportItemViewModel>
      | SorterResult<BillingAndProfitByCustomerReportItemViewModel>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<BillingAndProfitByCustomerReportItemViewModel>)
        .order || SortingDirectionEnum.Ascend;
    const sortBy = (
      sort as SorterResult<BillingAndProfitByCustomerReportItemViewModel>
    ).columnKey as BillingAndProfitSortFieldEnum;

    setSortParams(({ sortBy: prevSortBy, sortOrder: prevSortOrder }) => {
      if (prevSortBy !== sortBy || prevSortOrder !== sortOrder) {
        // Reset table page if sorting has changed
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }

      return {
        sortOrder,
        sortBy,
      };
    });
  };

  const handleSelectDate =
    (type: BillingProfitFilterTypes) => (date: RangeValue<Moment>) => {
      if (date) {
        setFilters({
          ...filters,
          [type]: {
            startDate: date[0],
            endDate: date[1],
          },
        });
      } else {
        setFilters({
          ...filters,
          [type]: undefined,
        });
      }
      setCurrentTablePage(1);
    };

  const handleDownloadReport = async () => {
    const result = await dispatch(
      getBillingProfitReportExcel({
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[BillingProfitFilterTypes.Date]?.startDate
          ? getDateWithoutTime(
              filters[BillingProfitFilterTypes.Date]?.startDate,
            )
          : undefined,
        endDate: filters[BillingProfitFilterTypes.Date]?.endDate
          ? getDateWithoutTime(filters[BillingProfitFilterTypes.Date]?.endDate)
          : undefined,
      }),
    );

    if (getBillingProfitReportExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {billingProfitReportData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={billingProfitReportData?.lastSyncDate}
                title="Billing and profit by customer report is automatically refreshed every 15 minutes."
              />
            </div>
          )}
        </BasePageLabel>
        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              enableSubTitle={isInitialDataLoaded}
              subTitle={billingAndProfitReportCountMessage}
              title="Billing & Profit by Customer"
            >
              <div>
                <DatePicker.RangePicker
                  format="MM/DD/YYYY"
                  separator="-"
                  className="d-flex"
                  allowClear={false}
                  disabledDate={disableDatesExceptSundays}
                  value={
                    filters[BillingProfitFilterTypes.Date]
                      ? [
                          moment(
                            filters[BillingProfitFilterTypes.Date]?.startDate,
                          ),
                          moment(
                            filters[BillingProfitFilterTypes.Date]?.endDate,
                          ),
                        ]
                      : undefined
                  }
                  onChange={handleSelectDate(BillingProfitFilterTypes.Date)}
                />
              </div>
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>
            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: billingProfitReportData?.totalCount,
                pageSize: pageDefaultSizeTable,
                current: currentTablePage,
                showSizeChanger: false,
              }}
              summary={() => (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={1}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      {billingProfitReportData?.grandTotal?.billingTotal}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      {billingProfitReportData?.grandTotal?.profitTotal}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      {
                        billingProfitReportData?.grandTotal
                          ?.percentageOfProfitTotal
                      }
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              )}
              showSorterTooltip={false}
              onChange={handleTableChange}
              columns={sortColumns}
              dataSource={billingProfitReportData?.items}
              rowKey={(record) => record?.customerKey as string}
              loading={isBillingProfitReportDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
